import { SUPABASE_ANON_KEY, SUPABASE_URL } from './supabase-config.js';

const CODE_RE = /^[A-Za-z0-9_-]{20,64}$/;

/** 128 bits of unguessable capability id, encoded without URL punctuation. */
export function createInventoryShareCode(cryptoApi = globalThis.crypto) {
  if (!cryptoApi?.getRandomValues) throw new Error('Secure link creation is unavailable on this device.');
  const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const encoded = typeof btoa === 'function' ? btoa(binary) : Buffer.from(bytes).toString('base64');
  return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

export function inventoryShareUrl(code, origin = globalThis.location?.origin) {
  const clean = String(code ?? '').trim();
  if (!CODE_RE.test(clean)) throw new Error('Inventory request code is invalid.');
  return new URL(`/i/${clean}`, origin).toString();
}

export function inventoryShareCodeFromLocation(locationLike = globalThis.location) {
  const match = String(locationLike?.pathname ?? '').match(
    /^\/(?:i|client-inventory)\/([A-Za-z0-9_-]{20,64})\/?$/,
  );
  if (match?.[1]) return match[1];
  const query = new URLSearchParams(String(locationLike?.search ?? '')).get('request') ?? '';
  return CODE_RE.test(query) ? query : null;
}

/** Publish through the signed-in designer's existing Supabase client. */
export async function publishInventoryShareLink(client, payload, { code = createInventoryShareCode() } = {}) {
  if (!client?.rpc) throw new Error('Sign in to create a short inventory link.');
  if (!CODE_RE.test(code)) throw new Error('Inventory request code is invalid.');
  const { data, error } = await client.rpc('create_inventory_share_link', {
    p_code: code,
    p_payload: payload,
  });
  // platform-boundary-exception: re-throws the RPC message with a readable
  // fallback so the CALLER decides, no retry-policy branch — see
  // platform/boundary-exceptions.json.
  if (error) throw new Error(error.message || 'The short inventory link could not be created.');
  const stored = String(data ?? code).trim();
  if (!CODE_RE.test(stored)) throw new Error('The short inventory link could not be confirmed.');
  return stored;
}

/** Resolve one opaque code without granting anonymous access to the backing table. */
export async function resolveInventoryShareLink(code, { fetchImpl = globalThis.fetch } = {}) {
  const clean = String(code ?? '').trim();
  if (!CODE_RE.test(clean)) throw new Error('This inventory link is invalid or incomplete.');
  if (typeof fetchImpl !== 'function') throw new Error('This inventory request needs an internet connection the first time it is opened.');
  const response = await fetchImpl(`${SUPABASE_URL}/rest/v1/rpc/resolve_inventory_share_link`, {
    method: 'POST',
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ p_code: clean }),
  });
  if (!response.ok) throw new Error('This inventory request could not be opened. Ask the designer for a new link.');
  const payload = await response.json();
  if (!payload) throw new Error('This inventory request has expired or is unavailable. Ask the designer for a new link.');
  return payload;
}
